import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * A size grid that adds a specific variant to the cart in one click.
 *
 * Horizon's quick add opens a modal for anything with more than one option,
 * which is every apparel product here. This trades that modal for the sizes
 * themselves: the panel slides up over the card image on hover and each size
 * is a button that submits the form directly.
 *
 * Every variant is rendered up front, tagged with its colour, and the ones
 * that do not belong to the selected colour are hidden. That keeps a colour
 * swatch change local — no refetch, no re-render, and the grid is correct
 * before the first paint rather than after a round trip.
 */
class DcrQuickAdd extends Component {
  /** @type {Element} */
  #eventHost = this;

  connectedCallback() {
    super.connectedCallback();

    // The colour swatches are a sibling of this element, not a child, so their
    // event never passes through us. It does bubble to the product card, which
    // is the nearest ancestor both live inside.
    this.#eventHost = this.closest('product-card') ?? this;
    this.#eventHost.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.#syncColor(this.dataset.selectedColor ?? '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#eventHost.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /** @returns {HTMLButtonElement[]} */
  get #sizeButtons() {
    return /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[data-variant-id]')));
  }

  /**
   * Adds the clicked size to the cart.
   * @param {Event} event - The click event.
   */
  addVariant = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = /** @type {HTMLButtonElement | null} */ (target.closest('[data-variant-id]'));
    if (!button || button.disabled) return;

    const form = this.querySelector('form');
    const variantInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]') ?? null);
    if (!form || !variantInput) return;

    variantInput.value = button.dataset.variantId ?? '';

    // requestSubmit rather than submit: it fires the submit event, which is what
    // product-form-component intercepts. A bare submit() would bypass it and
    // navigate the page to /cart/add.
    form.requestSubmit();
  };

  /**
   * Re-filters the grid when a colour swatch changes the selected variant.
   * @param {Event} event - The product select event.
   */
  #handleProductSelect = (event) => {
    const detail = /** @type {{ variantId?: string }} */ (
      /** @type {CustomEvent} */ (event).detail ?? {}
    );
    if (!detail.variantId) return;

    const selected = this.#sizeButtons.find((button) => button.dataset.variantId === String(detail.variantId));
    if (!selected) return;

    this.#syncColor(selected.dataset.color ?? '');
  };

  /**
   * Shows only the sizes belonging to a colour.
   * @param {string} color - The colour to show.
   */
  #syncColor(color) {
    this.dataset.selectedColor = color;

    for (const button of this.#sizeButtons) {
      const belongs = !color || (button.dataset.color ?? '') === color;
      button.hidden = !belongs;
    }
  }
}

if (!customElements.get('dcr-quick-add')) {
  customElements.define('dcr-quick-add', DcrQuickAdd);
}

/**
 * The touch trigger for the size row.
 *
 * It cannot bind to the panel declaratively: the plus sits beside the card's
 * title, outside the gallery, and the panel sits inside it — the two are
 * siblings' descendants, not ancestor and child. The product card is the
 * nearest thing they share.
 */
class DcrQuickAddTrigger extends Component {
  /**
   * Shows or hides the size row for this card.
   * @param {Event} event - The click event.
   */
  toggle = (event) => {
    event.preventDefault();

    const card = this.closest('product-card') ?? this.parentElement;
    const panel = card?.querySelector('dcr-quick-add');
    const button = this.querySelector('button');
    if (!panel || !button) return;

    const open = panel.getAttribute('data-open') === 'true';
    panel.setAttribute('data-open', String(!open));
    button.setAttribute('aria-expanded', String(!open));
  };
}

if (!customElements.get('dcr-quick-add-trigger')) {
  customElements.define('dcr-quick-add-trigger', DcrQuickAddTrigger);
}
